/**
 * Single source of truth for confidence display across the platform.
 *
 * All confidence values originate at the document parse / OCR stage and are
 * aggregated by the deal-intelligence orchestrator into `confidence_engine.lane_breakdown`.
 * UI surfaces MUST read from those canonical values — never recompute, restate,
 * or "approximate" them.
 *
 * Display rules:
 *  - Always render as a whole number (no decimals)
 *  - One unified label tier:
 *      80–100 → "High"
 *      50–79  → "Moderate"
 *      <50    → "Low"
 */

export type ConfidenceTier = "High" | "Moderate" | "Low";

export function normalizeConfidenceScore(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  const num = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(num)) return null;
  return Math.round(Math.max(0, Math.min(100, num)));
}

export function confidenceTier(score: number | null | undefined): ConfidenceTier {
  const s = normalizeConfidenceScore(score) ?? 0;
  if (s >= 80) return "High";
  if (s >= 50) return "Moderate";
  return "Low";
}

/** "High confidence (84/100)" / "Low confidence (40/100)" */
export function formatConfidence(score: number | null | undefined): string {
  const s = normalizeConfidenceScore(score);
  if (s === null) return "—";
  return `${confidenceTier(s)} confidence (${s}/100)`;
}

/** Compact form for badges: "84/100" */
export function formatConfidenceBadge(score: number | null | undefined): string {
  const s = normalizeConfidenceScore(score);
  return s === null ? "—" : `${s}/100`;
}

/**
 * Canonical lane confidence accessor. Reads the per-lane integer
 * `confidence_score` from `confidence_engine.lane_breakdown` produced by
 * the deal-intelligence orchestrator. Returns null when the lane is absent.
 */
export interface LaneConfidenceEntry {
  lane: string;
  label?: string;
  document_count?: number;
  trust_score?: number | null;
  confidence_score?: number | null;
}

export interface ConfidenceEngineShape {
  lane_breakdown?: LaneConfidenceEntry[] | null;
  overall_score?: number | null;
}

export function getLaneConfidence(
  engine: ConfidenceEngineShape | null | undefined,
  lane: "rent_roll" | "t12" | "supporting_document" | string,
): number | null {
  const entry = engine?.lane_breakdown?.find((l) => l.lane === lane);
  return normalizeConfidenceScore(entry?.confidence_score);
}

/**
 * Rewrite narrative copy so any embedded confidence figures (e.g. "44%
 * confidence", "44/100", "Source confidence is only 44") reference the
 * canonical lane value from `confidence_engine.lane_breakdown` instead of
 * stale per-document or per-metric scores. Narrative text is otherwise
 * preserved verbatim.
 *
 * Heuristic: if the sentence/segment mentions occupancy, rent roll, or
 * in-place rent, the rent_roll lane value wins. If it mentions T12,
 * operating, NOI, expense ratio, or collections, the t12 lane value wins.
 * Sentences without either signal are left untouched.
 */
export function rewriteNarrativeConfidence(
  text: string | null | undefined,
  engine: ConfidenceEngineShape | null | undefined,
): string {
  if (!text) return "";
  const rentRoll = getLaneConfidence(engine, "rent_roll");
  const t12 = getLaneConfidence(engine, "t12");
  if (rentRoll === null && t12 === null) return text;

  const rentRollSignal = /(occupanc|rent\s*roll|in[- ]place rent|leas)/i;
  const t12Signal = /(t-?12|operating|noi|expense ratio|collections|revenue)/i;

  // Operate sentence-by-sentence so each clause picks its own lane.
  return text.split(/(?<=[.!?])\s+/).map((sentence) => {
    const isRentRoll = rentRollSignal.test(sentence);
    const isT12 = !isRentRoll && t12Signal.test(sentence);
    const canonical = isRentRoll ? rentRoll : isT12 ? t12 : null;
    if (canonical === null) return sentence;

    let out = sentence;
    // "44/100"  →  "<canonical>/100"
    out = out.replace(/\b(\d{1,3})\s*\/\s*100\b/g, `${canonical}/100`);
    // "44% confidence"  →  "<canonical>% confidence"
    out = out.replace(/\b(\d{1,3})\s*%\s*confidence\b/gi, `${canonical}% confidence`);
    // "confidence is only 44" / "confidence of 44" / "carries 44 confidence"
    out = out.replace(/\bconfidence\s+(is|of|at|only)\s+(\d{1,3})\b/gi, (_m, q) => `confidence ${q} ${canonical}`);
    out = out.replace(/\b(Source confidence is only)\s+(\d{1,3})\b/gi, `$1 ${canonical}`);
    return out;
  }).join(" ");
}
